/**
 * Editor de assessments (marcos de evaluación). Solo admin.
 *
 * Cada cambio en inputs/scoring/custom_formula incrementa current_version
 * y deja snapshot inmutable en assessment_versions. Las versiones se ven
 * en el detalle (TODO en iteración 2).
 *
 * Para esta entrega: listar + crear + editar inputs/pesos/scoring + toggle.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState, forwardRef } from "react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, LayoutGrid, Plus, Trash2, Settings2, History } from "lucide-react";
import {
  EVAL_LEVELS,
  EVAL_LEVEL_LABELS,
  EVAL_VISIBILITIES,
  EVAL_VISIBILITY_LABELS,
  EVAL_RECURRENCES,
  EVAL_RECURRENCE_LABELS,
  APPLIES_TO_PROJECTS_OPTS,
  APPLIES_TO_PROJECTS_LABELS,
  ASSESSMENT_SCORING_METHODS,
  ASSESSMENT_SCORING_METHOD_LABELS,
  assessmentCreateSchema,
  type AssessmentCreateInput,
  type Assessment,
  type AssessmentInput,
  type KpiDefinition,
  type Questionnaire,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";

export const Route = createFileRoute("/admin/assessments")({
  component: AssessmentsAdminPage,
});

function AssessmentsAdminPage() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery<{ assessments: Assessment[] }>({
    queryKey: ["assessments", "all"],
    queryFn: () => api.get("/assessments?includeInactive=1"),
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/assessments/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["assessments"] }),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="admin"
        title="Evaluaciones (marcos)"
        subtitle="Cada marco combina KPIs y cuestionarios vía scoring_method. Cambios bumpean la versión y se guarda snapshot inmutable para reconstrucción evolutiva."
        actions={
          <div className="flex items-center gap-2">
            <Link to="/admin" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Volver
            </Link>
            <button
              type="button"
              onClick={() => dialogRef.current?.showModal()}
              className="btn btn-primary"
            >
              <Plus className="size-4" /> Nuevo marco
            </button>
          </div>
        }
      />

      {isLoading && (
        <div className="grid place-items-center py-20">
          <span className="loading loading-spinner loading-lg text-primary" />
        </div>
      )}

      {data && data.assessments.length === 0 ? (
        <div className="card-elevated py-16 text-center">
          <LayoutGrid className="size-12 text-primary/60 mx-auto mb-3" />
          <p className="text-sm text-base-content/60">Sin marcos aún. Crea el primero.</p>
        </div>
      ) : (
        <ul className="space-y-3">
          {data?.assessments.map((a) => (
            <AssessmentRow
              key={a.id}
              assessment={a}
              onToggle={() => toggleActive.mutate({ id: a.id, active: !a.active })}
            />
          ))}
        </ul>
      )}

      <CreateAssessmentDialog ref={dialogRef} onClose={() => dialogRef.current?.close()} />
    </div>
  );
}

function AssessmentRow({ assessment, onToggle }: { assessment: Assessment; onToggle: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="card-elevated p-4">
      <header className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className={`badge badge-sm ${levelBadgeClass(assessment.level)}`}>
              {EVAL_LEVEL_LABELS[assessment.level]}
            </span>
            <span className="badge badge-ghost badge-sm">
              {EVAL_VISIBILITY_LABELS[assessment.visibility]}
            </span>
            <span className="badge badge-ghost badge-sm">
              {EVAL_RECURRENCE_LABELS[assessment.recurrence]}
            </span>
            <span className="badge badge-secondary badge-sm gap-1">
              <History className="size-3" /> v{assessment.currentVersion}
            </span>
            {!assessment.active && <span className="badge badge-error badge-sm">inactivo</span>}
          </div>
          <h3 className="font-display font-semibold">{assessment.name}</h3>
          {assessment.description && (
            <p className="text-sm text-base-content/65 mt-1 line-clamp-2">
              {assessment.description}
            </p>
          )}
          <div className="text-xs text-base-content/55 mt-1.5">
            scoring:{" "}
            <span className="font-medium">
              {ASSESSMENT_SCORING_METHOD_LABELS[assessment.scoringMethod]}
            </span>
          </div>
        </div>
        <button type="button" onClick={onToggle} className="btn btn-ghost btn-sm">
          {assessment.active ? "Desactivar" : "Activar"}
        </button>
        <button type="button" onClick={() => setOpen(!open)} className="btn btn-ghost btn-sm gap-1">
          <Settings2 className="size-4" /> Editar
        </button>
      </header>
      {open && <AssessmentEditor assessment={assessment} onClose={() => setOpen(false)} />}
    </li>
  );
}

function AssessmentEditor({
  assessment,
  onClose,
}: {
  assessment: Assessment;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  // Cargar inputs actuales + listas de KPIs y cuestionarios para el selector.
  const detailQ = useQuery<{ assessment: Assessment; inputs: AssessmentInput[] }>({
    queryKey: ["assessment", assessment.id, "detail"],
    queryFn: () => api.get(`/assessments/${assessment.id}`),
  });
  const kpisQ = useQuery<{ definitions: KpiDefinition[] }>({
    queryKey: ["kpi-definitions"],
    queryFn: () => api.get(`/kpi-definitions?level=${assessment.level}`),
  });
  const qsQ = useQuery<{ templates: Questionnaire[] }>({
    queryKey: ["questionnaires-by-level", assessment.level],
    queryFn: () => api.get(`/questionnaires`),
  });

  const filteredQs = useMemo(
    () => (qsQ.data?.templates ?? []).filter((q) => q.level === assessment.level && q.active),
    [qsQ.data, assessment.level],
  );
  const filteredKpis = useMemo(
    () => (kpisQ.data?.definitions ?? []).filter((d) => d.active),
    [kpisQ.data],
  );

  type Row = {
    id: string;
    inputKind: "kpi" | "questionnaire";
    inputId: string;
    weight: number;
    required: boolean;
  };

  const [rows, setRows] = useState<Row[]>([]);
  // Hidratar cuando lleguen los inputs.
  useMemo(() => {
    if (detailQ.data?.inputs) {
      setRows(
        detailQ.data.inputs.map((i) => ({
          id: i.id,
          inputKind: i.inputKind,
          inputId: i.inputId,
          weight: i.weight,
          required: i.required,
        })),
      );
    }
  }, [detailQ.data]);

  const [scoringMethod, setScoringMethod] = useState(assessment.scoringMethod);

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/assessments/${assessment.id}`, {
        scoringMethod,
        inputs: rows.map((r) => ({
          inputKind: r.inputKind,
          inputId: r.inputId,
          weight: r.weight,
          required: r.required,
        })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["assessments"] });
      qc.invalidateQueries({ queryKey: ["assessment", assessment.id, "detail"] });
      onClose();
    },
  });

  if (detailQ.isLoading) {
    return (
      <div className="grid place-items-center py-4 mt-3 border-t border-base-300/40">
        <span className="loading loading-spinner loading-sm" />
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-base-300/40 space-y-3">
      <div>
        <label className="label py-1">
          <span className="label-text text-sm font-medium">Scoring method</span>
        </label>
        <select
          value={scoringMethod}
          onChange={(e) => setScoringMethod(e.target.value as typeof scoringMethod)}
          className="select select-bordered select-sm w-full max-w-md"
        >
          {ASSESSMENT_SCORING_METHODS.map((m) => (
            <option key={m} value={m}>
              {ASSESSMENT_SCORING_METHOD_LABELS[m]}
            </option>
          ))}
        </select>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <h4 className="text-sm font-semibold">Inputs ({rows.length})</h4>
          <div className="flex gap-1">
            <button
              type="button"
              className="btn btn-ghost btn-xs gap-1"
              onClick={() =>
                setRows([
                  ...rows,
                  {
                    id: crypto.randomUUID(),
                    inputKind: "kpi",
                    inputId: "",
                    weight: 1.0,
                    required: false,
                  },
                ])
              }
            >
              <Plus className="size-3" /> KPI
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-xs gap-1"
              onClick={() =>
                setRows([
                  ...rows,
                  {
                    id: crypto.randomUUID(),
                    inputKind: "questionnaire",
                    inputId: "",
                    weight: 1.0,
                    required: false,
                  },
                ])
              }
            >
              <Plus className="size-3" /> Cuestionario
            </button>
          </div>
        </div>
        <ul className="space-y-2">
          {rows.map((r, idx) => (
            <li key={r.id} className="flex items-center gap-2 flex-wrap">
              <span
                className={`badge badge-xs ${r.inputKind === "kpi" ? "badge-secondary" : "badge-accent"}`}
              >
                {r.inputKind === "kpi" ? "KPI" : "Cuest."}
              </span>
              <select
                value={r.inputId}
                onChange={(e) =>
                  setRows(rows.map((x, i) => (i === idx ? { ...x, inputId: e.target.value } : x)))
                }
                className="select select-bordered select-xs flex-1 min-w-0"
              >
                <option value="">Selecciona…</option>
                {r.inputKind === "kpi"
                  ? filteredKpis.map((k) => (
                      <option key={k.id} value={k.id}>
                        {k.name}
                      </option>
                    ))
                  : filteredQs.map((q) => (
                      <option key={q.id} value={q.id}>
                        {q.name}
                      </option>
                    ))}
              </select>
              <label className="text-xs text-base-content/55 flex items-center gap-1">
                peso
                <input
                  type="number"
                  step="0.05"
                  min="0"
                  value={r.weight}
                  onChange={(e) =>
                    setRows(
                      rows.map((x, i) =>
                        i === idx ? { ...x, weight: Number(e.target.value) } : x,
                      ),
                    )
                  }
                  className="input input-bordered input-xs w-16 text-right tabular-nums"
                />
              </label>
              <label className="text-xs flex items-center gap-1">
                <input
                  type="checkbox"
                  checked={r.required}
                  onChange={(e) =>
                    setRows(
                      rows.map((x, i) => (i === idx ? { ...x, required: e.target.checked } : x)),
                    )
                  }
                  className="checkbox checkbox-xs"
                />
                req
              </label>
              <button
                type="button"
                onClick={() => setRows(rows.filter((_, i) => i !== idx))}
                className="btn btn-ghost btn-xs btn-square"
                title="Quitar"
              >
                <Trash2 className="size-3.5 text-error" />
              </button>
            </li>
          ))}
        </ul>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onClose} className="btn btn-ghost btn-sm">
          Cancelar
        </button>
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="btn btn-primary btn-sm"
        >
          {save.isPending && <span className="loading loading-spinner loading-xs" />}
          Guardar (bump version)
        </button>
      </div>
    </div>
  );
}

// ─── Create dialog ────────────────────────────────────────────────────────

const CreateAssessmentDialog = forwardRef<HTMLDialogElement, { onClose: () => void }>(
  function CreateAssessmentDialog({ onClose }, ref) {
    const qc = useQueryClient();
    const {
      register,
      handleSubmit,
      reset,
      control,
      formState: { errors },
    } = useForm<AssessmentCreateInput>({
      resolver: zodResolver(assessmentCreateSchema),
      defaultValues: {
        level: "project",
        scoringMethod: "weighted_average",
        recurrence: "monthly",
        visibility: "everyone",
        appliesToProjects: "all",
        active: true,
        inputs: [],
      },
    });

    const items = useFieldArray({ control, name: "inputs" });

    const create = useMutation({
      mutationFn: (input: AssessmentCreateInput) => api.post("/assessments", input),
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: ["assessments"] });
        reset();
        onClose();
      },
    });

    return (
      <dialog ref={ref} className="modal" onClose={() => reset()}>
        <div className="modal-box card-elevated max-w-2xl">
          <h3 className="text-lg font-display font-semibold mb-4">Nuevo marco de evaluación</h3>
          <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-3">
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Nombre</span>
              </label>
              <input
                className="input input-bordered w-full"
                placeholder="Ej. Salud del Proyecto"
                {...register("name")}
              />
              {errors.name && (
                <span className="text-xs text-error mt-1 block">{errors.name.message}</span>
              )}
            </div>
            <div>
              <label className="label py-1">
                <span className="label-text text-sm font-medium">Descripción</span>
              </label>
              <textarea
                rows={2}
                className="textarea textarea-bordered w-full"
                {...register("description")}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="label py-1">
                  <span className="label-text text-sm font-medium">Nivel</span>
                </label>
                <select className="select select-bordered w-full" {...register("level")}>
                  {EVAL_LEVELS.map((l) => (
                    <option key={l} value={l}>
                      {EVAL_LEVEL_LABELS[l]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label py-1">
                  <span className="label-text text-sm font-medium">Scoring</span>
                </label>
                <select className="select select-bordered w-full" {...register("scoringMethod")}>
                  {ASSESSMENT_SCORING_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {ASSESSMENT_SCORING_METHOD_LABELS[m]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label py-1">
                  <span className="label-text text-sm font-medium">Recurrencia</span>
                </label>
                <select className="select select-bordered w-full" {...register("recurrence")}>
                  {EVAL_RECURRENCES.map((r) => (
                    <option key={r} value={r}>
                      {EVAL_RECURRENCE_LABELS[r]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="label py-1">
                  <span className="label-text text-sm font-medium">Visibilidad</span>
                </label>
                <select className="select select-bordered w-full" {...register("visibility")}>
                  {EVAL_VISIBILITIES.map((v) => (
                    <option key={v} value={v}>
                      {EVAL_VISIBILITY_LABELS[v]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="label py-1">
                  <span className="label-text text-sm font-medium">Aplica a proyectos</span>
                </label>
                <select
                  className="select select-bordered w-full"
                  {...register("appliesToProjects")}
                >
                  {APPLIES_TO_PROJECTS_OPTS.map((o) => (
                    <option key={o} value={o}>
                      {APPLIES_TO_PROJECTS_LABELS[o]}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="pt-2 border-t border-base-300/60">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-semibold">
                  Inputs (opcional, también editable después)
                </h4>
                <div className="flex gap-1">
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs gap-1"
                    onClick={() =>
                      items.append({
                        inputKind: "kpi",
                        inputId: "",
                        weight: 1.0,
                        required: false,
                      })
                    }
                  >
                    <Plus className="size-3" /> KPI
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs gap-1"
                    onClick={() =>
                      items.append({
                        inputKind: "questionnaire",
                        inputId: "",
                        weight: 1.0,
                        required: false,
                      })
                    }
                  >
                    <Plus className="size-3" /> Cuestionario
                  </button>
                </div>
              </div>
              <p className="text-xs text-base-content/55">
                Puedes dejarlo vacío y agregar inputs después en el editor inline.
              </p>
              {items.fields.map((f, idx) => (
                <Controller
                  key={f.id}
                  control={control}
                  name={`inputs.${idx}.inputKind`}
                  render={({ field }) => (
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span
                        className={`badge badge-xs ${field.value === "kpi" ? "badge-secondary" : "badge-accent"}`}
                      >
                        {field.value === "kpi" ? "KPI" : "Cuest."}
                      </span>
                      <input
                        placeholder="ID (e.g. seed_m_x)"
                        className="input input-bordered input-xs flex-1 font-mono"
                        {...register(`inputs.${idx}.inputId`)}
                      />
                      <input
                        type="number"
                        step="0.05"
                        placeholder="peso"
                        className="input input-bordered input-xs w-20 text-right tabular-nums"
                        {...register(`inputs.${idx}.weight`, { valueAsNumber: true })}
                      />
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs btn-square"
                        onClick={() => items.remove(idx)}
                      >
                        <Trash2 className="size-3.5 text-error" />
                      </button>
                    </div>
                  )}
                />
              ))}
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-3 border-t border-base-300/60">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  reset();
                  onClose();
                }}
              >
                Cancelar
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={create.isPending}>
                {create.isPending && <span className="loading loading-spinner loading-xs" />}
                Crear marco
              </button>
            </div>
          </form>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button>cerrar</button>
        </form>
      </dialog>
    );
  },
);

function levelBadgeClass(level: Assessment["level"]): string {
  switch (level) {
    case "client_user":
      return "badge-info";
    case "organizational":
      return "badge-secondary";
    case "team":
      return "badge-accent";
    case "project":
      return "badge-primary";
  }
}
